import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, Trophy } from "lucide-react";
import { VoteButton } from "./VoteButton";
import { useNavigate } from "react-router-dom";

interface LeaderboardItem {
  listing_id: string;
  listing_name: string;
  listing_slug: string;
  listing_address?: string;
  listing_thumbnail?: string;
  listing_rating?: number;
  all_time_votes: number;
  rank: number;
}

interface LeaderboardTop3Props {
  items: LeaderboardItem[];
  citySlug: string;
  activitySlug: string;
}

const medalColors = {
  1: "bg-gradient-to-br from-yellow-400 to-yellow-600",
  2: "bg-gradient-to-br from-gray-300 to-gray-500",
  3: "bg-gradient-to-br from-orange-400 to-orange-600"
};

const medalIcons = {
  1: "🥇",
  2: "🥈",
  3: "🥉"
};

export const LeaderboardTop3 = ({ items, citySlug, activitySlug }: LeaderboardTop3Props) => {
  const navigate = useNavigate();
  
  if (!items || items.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No hay lugares votados todavía. ¡Sé el primero en votar!</p>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-3 gap-6 mb-12">
      {items.map((item) => (
        <Card 
          key={item.listing_id}
          className={`relative overflow-hidden cursor-pointer hover:shadow-2xl transition-all duration-300 ${
            item.rank === 1 ? 'md:scale-105 border-2 border-yellow-500' : ''
          }`}
          onClick={() => navigate(`/listing/${item.listing_slug}`)}
        >
          {/* Medal Badge */}
          <div className="absolute top-4 right-4 z-10">
            <div className={`${medalColors[item.rank as keyof typeof medalColors]} text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg`}>
              {medalIcons[item.rank as keyof typeof medalIcons]} #{item.rank}
            </div>
          </div>

          {/* Image - ALWAYS SHOW with fallback */}
          <div className="relative h-48 overflow-hidden bg-muted">
            <img
              src={item.listing_thumbnail || '/default-listing-fallback.png'}
              alt={item.listing_name}
              className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
              onError={(e) => {
                e.currentTarget.src = '/default-listing-fallback.png';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>

          <CardHeader>
            <CardTitle className="text-xl flex items-start gap-2">
              <Trophy className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
              <span>{item.listing_name}</span>
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            {item.listing_address && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span className="line-clamp-1">{item.listing_address}</span>
              </div>
            )}

            {item.listing_rating && item.listing_rating > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-4 h-4 fill-primary text-primary" />
                <span className="font-semibold">{item.listing_rating.toFixed(1)}</span>
              </div>
            )}

            <div className="space-y-3">
              <div className="text-center">
                <Badge variant="secondary" className="text-base px-4 py-2">
                  {item.all_time_votes} votos
                </Badge>
              </div>
              
              <div className="pt-3 border-t" onClick={(e) => e.stopPropagation()}>
                <VoteButton
                  listingId={item.listing_id}
                  citySlug={citySlug}
                  activitySlug={activitySlug}
                  currentVotes={item.all_time_votes}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
